import { downloadToCSV, getTimezone, useTranslation } from '@fareye/common'
import {
  Button,
  Card,
  Flex,
  Grid,
  Label,
  RenderControl,
  Span,
  styled
} from '@fareye/ui'
import {
  cancelToken,
  cloneDeep,
  dMmm,
  FaCheckCircle,
  FaDownload,
  get,
  isEmpty,
  startCase
} from '@fareye/utils'
import React, { useMemo } from 'react'
import { ColorSpan, style } from '../../../style/styled'
import { InitialState } from '../../../utility/constants'
import { displayTime } from '../../../utility/function'
import VerticalTimeLine from './VerticalTimeLine'
const InfoSpan = styled(Span)`
  margin-bottom: 10px;
`
const EtaLabel = styled(Label)`
  font-weight: normal;
`

export default function TrackingDetail({ config, data, baseUrl, match }: any) {
  const { t } = useTranslation()
  const [timelineState, setTimeLineState] = React.useState(InitialState)
  const {
    milestoneCarrierStatus,
    trackingId,
    packageStatus,
    exceptions,
    eta,
    sta
  } = data
  const { slug }: any = match.params
  const source = cancelToken().source()
  React.useEffect(() => {
    fetchTimeLineData()
    return () => source.cancel('')
  }, [])

  async function fetchTimeLineData() {
    try {
      setTimeLineState({ ...timelineState, isLoading: true })
      const { timeLineUrl, isShared } = config?.config
      const headers: any = {}
      if (config.config.isShared == 'true') {
        headers['X-Fe-Timezone'] = getTimezone()
      }
      const shareableSlug = isShared ? slug : trackingId
      const { data } = await get(
        `${baseUrl}${timeLineUrl}/${shareableSlug}`,
        source.token,
        { headers: headers }
      )
      setTimeLineState({
        ...timelineState,
        isLoading: false,
        data: typeof data == 'object' ? data : [],
        isSuccess: true,
        isError: false,
        message: isEmpty(data)
          ? `${t('No Data Found')}...`
          : typeof data == 'string'
          ? data
          : ''
      })
    } catch (ex:any) {
      setTimeLineState({
        ...timelineState,
        isLoading: false,
        isSuccess: false,
        isError: true,
        message: `${t('Something went wrong')}...`
      })
      console.error(ex)
    }
  }
  const isDisabled = useMemo(() => {
    const data = timelineState?.data
      ? (timelineState as any).data.reduce((a: any[], b: any) => {
          if (b.rawStatusData) {
            a = a.concat(b.rawStatusData)
          }
          return a
        }, [])
      : []
    return isEmpty(data)
  }, [timelineState.data])
  function downloadReport() {
    try {
      if (timelineState.data) {
        const data = (timelineState as any).data.reduce((a: any[], b: any) => {
          if (b.rawStatusData) {
            let rawStatusDataArray = cloneDeep(b.rawStatusData)
            rawStatusDataArray.map((element: any) => {
              delete element['dateTimeObj']
              return element
            })
            a = a.concat(rawStatusDataArray)
          }
          return a
        }, [])
        let translatedData = data
        translatedData = translatedData.map((item: any) => {
          return {
            ...item,
            activity: t(item.activity),
            status: t(item.status)
          }
        })
        downloadToCSV({
          data: translatedData,
          fileName: 'tracking Data' || `${dMmm()}`,
          extension: 'csv'
        })
      }
    } catch (ex:any) {}
  }
  return (
    <Grid bg="white">
      <Card
        style={{ boxShadow: '0 2px 4px 0 rgba(33,33,33,0.25)' }}
        bodyStyle={{ padding: '18px' }}
      >
        <Flex style={{ marginBottom: '18px' }} justifyContentSpaceBetween>
          <Label>{`${t('Parcel Tracking Timeline')}`}</Label>
          <Button
            variant="s"
            style={
              isDisabled
                ? {}
                : {
                    color: ' #006491',
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontWeight: '500',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 25%)',
                    padding: '7px 10px'
                  }
            }
            onClick={() => {
              downloadReport()
            }}
            disabled={isDisabled}
          >
            <FaDownload style={style.downloadIcon} />
            {t('Download Report')}
          </Button>
        </Flex>
        <Grid gridGap="20px" gridTemplateColumns="auto 110px 210px 100px">
          <Flex column>
            <InfoSpan variant="secondary">{t('Current Status')}</InfoSpan>
            <Flex
              style={{
                height: '100%',
                borderRight: '1px solid #dadada'
              }}
              alignItemsCenter
              justifyContentFlexStart
            >
              {milestoneCarrierStatus && (
                <FaCheckCircle
                  color="#409e33"
                  size={style.iconSize}
                  style={{ background: '#fff' }}
                />
              )}
              <Label style={{ marginLeft: '4px' }} fontSize={16}>
                {t(milestoneCarrierStatus)}
              </Label>
            </Flex>
          </Flex>
          <Flex column>
            <InfoSpan variant="secondary">{t('Trip Status')}</InfoSpan>
            <ColorSpan
              style={{
                height: '100%',
                borderRight: '1px solid #dadada',
                fontWeight: '800'
              }}
              title={packageStatus}
            >
              {t(startCase(packageStatus))}
            </ColorSpan>
          </Flex>
          <Flex column>
            <InfoSpan variant="secondary">ETA / STA</InfoSpan>
            <Flex
              style={{
                height: '100%',
                borderRight: '1px solid #dadada'
              }}
              column
            >
              <EtaLabel style={{ marginBottom: '5px' }}>
                ETA : {eta ? displayTime(eta, 'ddMMMMYYYY') : '-'}
              </EtaLabel>
              <EtaLabel>
                STA : {sta ? displayTime(sta, 'ddMMMMYYYY') : '-'}
              </EtaLabel>
            </Flex>
          </Flex>
          <Flex column>
            <InfoSpan variant="secondary">{t('Exception')}</InfoSpan>
            <Label>{exceptions?.length}</Label>
          </Flex>
        </Grid>
      </Card>
      <RenderControl variant="l" state={timelineState}>
        <VerticalTimeLine data={data} timelineData={timelineState.data} />
      </RenderControl>
    </Grid>
  )
}
